import { Loader2, MessageSquare, Sparkles, FileText, PlusCircle, Mic, MicOff, AlertCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';
import { useNavigate } from 'react-router';
import { useGlobalContext } from '@/store';
import { apiClient } from '@/api/client';
import { ConsultationStepper } from '@/components/ConsultationStepper';
import { useRecaptcha } from '../useRecaptcha';
import { logTemporaryRemedies } from '@/utils/logTemporaryRemedies';
import { getOrCreateE2EEKey } from '@/utils/e2ee';
import { useSpeechToText } from '@/hooks/useSpeechToText';

const postDescription = async (
  data: { patient: Record<string, unknown>; description: string; mode?: string },
  config?: AxiosRequestConfig
) => {
  const response = await apiClient.post('/patients/description', data, config);
  return response.data;
};

export function Description() {
  const { patient, setToast, setErrorModal } = useGlobalContext();
  const navigate = useNavigate();
  const { getRequestConfig, getErrorMessage } = useRecaptcha();
  const [description, setDescription] = useState('');
  const [existingDescription, setExistingDescription] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const handleFinalTranscript = useCallback((textChunk: string) => {
    setDescription((prev) => {
      const trimmedPrev = prev.trim();
      const trimmedChunk = textChunk.trim();
      if (!trimmedPrev) return trimmedChunk;
      return `${trimmedPrev} ${trimmedChunk}`;
    });
  }, []);

  const {
    isListening,
    isSupported,
    interimTranscript,
    error: speechError,
    toggleListening,
  } = useSpeechToText({
    lang: 'en-US',
    onFinalTranscript: handleFinalTranscript,
  });

  useEffect(() => {
    getOrCreateE2EEKey().catch(() => {});
  }, []);

  useEffect(() => {
    if (patient?.email && patient?.name && patient?.consultationToken) {
      apiClient
        .get('/patients/by-email', {
          params: {
            email: patient.email,
            name: patient.name,
            consultation_token: patient.consultationToken,
          },
        })
        .then((res) => {
          if (res.data?.description) {
            setExistingDescription(res.data.description);
          } else {
            setExistingDescription('');
          }
        })
        .catch(() => setExistingDescription(''));
    } else {
      setExistingDescription('');
    }
  }, [patient?.email, patient?.name, patient?.consultationToken]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };

  const canSubmit = description.trim().length >= 10;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    if (!patient.email?.trim()) {
      setToast({ type: 'error', message: 'Please complete your Patient Account data first.' });
      return;
    }
    if (!patient.consultationToken) {
      setErrorModal({
        title: 'Missing consultation',
        message: 'Go to Account and click Start New Consultation before analyzing the description.',
      });
      return;
    }

    setSubmitting(true);
    try {
      const landingMode = ((import.meta.env.VITE_LANDING_MODE || 'pro') as string).toLowerCase().trim() as 'pro' | 'dtc';
      const fullText = existingDescription ? `${existingDescription.trim()}\n\n${description.trim()}` : description.trim();
      const recaptchaConfig = await getRequestConfig('description_submit');
      const res = await postDescription(
        {
          patient: {
            email: patient.email,
            name: patient.name,
            age: patient.age,
            gender: patient.gender,
            description_0: [],
            date_0: new Date().toISOString().slice(0, 10),
            consultation_token: patient.consultationToken,
          },
          description: fullText,
          mode: landingMode,
        },
        recaptchaConfig
      );

      if (res?.evaluation) {
        const effectiveMode = (res.mode || landingMode).toUpperCase();
        console.group(`🔍 Evaluación de Síntomas y Fiabilidad (Fase Descripción - Modo: ${effectiveMode})`);
        console.log(`Detalle de evaluación [Modo ${effectiveMode}] síntomas extraídos vs Base de Datos:`, res.evaluation);
        res.evaluation.forEach(
          (
            item: {
              extracted_symptom: string;
              candidate_matches?: Array<{ description: string; score: number; verified: boolean }>;
            },
            idx: number
          ) => {
            console.group(`Síntoma extraído #${idx + 1}: "${item.extracted_symptom}"`);
            if (item.candidate_matches && item.candidate_matches.length > 0) {
              console.table(
                item.candidate_matches.map(
                  (c: { description: string; score: number; verified: boolean }) => ({
                    'DB Symptom': c.description,
                    'Similarity Score (Fiabilidad)': (c.score * 100).toFixed(1) + '%',
                    'LLM Verified Exact': c.verified ? '✅ CONFIRMED' : '❌ REJECTED',
                  })
                )
              );
            } else {
              console.log('No se encontraron coincidencias en la base de datos.');
            }
            console.groupEnd();
          }
        );
        console.groupEnd();
      }

      await logTemporaryRemedies('Case Description', 2, patient);

      setToast({ type: 'success', message: 'Description analyzed and new symptoms added.' });
      if (existingDescription) {
        setExistingDescription((prev) => prev + '\n\n' + description);
      } else {
        setExistingDescription(description);
      }
      setDescription('');
      navigate('/physical-symptom');
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Error processing description. Please try again.');
      setErrorModal({ title: 'Error', message });
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, description, existingDescription, getErrorMessage, getRequestConfig, patient, setErrorModal, setToast, navigate]);

  return (
    <div className="mx-auto max-w-7xl">
      <ConsultationStepper />

      <div className="card bg-base-100 shadow-sm mb-4">
        <div className="card-body py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-accent text-accent-content rounded-full p-3">
                <MessageSquare size={28} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold">Step 2: Case Description</h2>
                  <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-xs px-2.5 py-0.5 rounded-full font-medium border border-emerald-300">
                    🔒 E2EE Encrypted
                  </span>
                </div>
                <p className="text-sm text-base-content/70">
                  Describe the ailment in your own words or use English voice dictation. Shared health data is protected via End-to-End Encryption under GDPR & HIPAA standards.
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Analyzing...
                </>
              ) : (
                <>
                  <Sparkles size={16} /> Analyze symptoms
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {existingDescription && (
            <div className="card bg-base-100 border border-info/30 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-center gap-2 text-info font-medium mb-2">
                  <FileText size={18} />
                  <span>Previously recorded description</span>
                </div>
                <p className="text-sm text-base-content/80 whitespace-pre-line bg-base-200/50 p-3 rounded-lg border border-base-200">
                  {existingDescription}
                </p>
              </div>
            </div>
          )}

          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <label className="label py-0" htmlFor="description-textarea">
                  <span className="label-text font-medium flex items-center gap-1.5">
                    {existingDescription ? <PlusCircle size={16} className="text-primary" /> : null}
                    {existingDescription ? 'Add additional text to description' : 'Describe your ailment'}
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={toggleListening}
                    disabled={!isSupported || submitting}
                    title={
                      isSupported
                        ? isListening
                          ? 'Stop dictation'
                          : 'Start English voice dictation'
                        : 'Voice dictation not supported in this browser'
                    }
                    className={`btn btn-sm ${
                      isListening
                        ? 'btn-error animate-pulse text-white shadow-md'
                        : 'btn-outline btn-secondary'
                    } flex items-center gap-1.5 min-h-[38px] px-3 font-medium transition-all`}
                  >
                    {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                    <span>{isListening ? 'Stop Dictating' : 'Dictate (EN)'}</span>
                  </button>
                  <span className="label-text-alt text-base-content/60 hidden sm:inline">
                    {description.length} chars (min 10)
                  </span>
                </div>
              </div>

              {isListening && (
                <div className="flex items-center gap-2 text-xs text-error font-medium my-1.5 px-1 animate-pulse">
                  <span className="w-2.5 h-2.5 rounded-full bg-error inline-block shrink-0"></span>
                  <span>Listening in English... Speak clearly into microphone</span>
                  {interimTranscript && (
                    <span className="text-base-content/70 italic ml-1 truncate max-w-xs sm:max-w-md">
                      "{interimTranscript}"
                    </span>
                  )}
                </div>
              )}

              {speechError && (
                <div className="alert alert-warning text-xs py-2 my-2 flex items-center gap-2">
                  <AlertCircle size={15} className="shrink-0" />
                  <span>{speechError}</span>
                </div>
              )}

              <textarea
                id="description-textarea"
                className="textarea textarea-bordered w-full min-h-[220px] text-base focus:textarea-primary transition-all"
                placeholder={
                  existingDescription
                    ? 'Add new details, changes in symptoms, or click "Dictate (EN)" to speak...'
                    : 'Example: I have a sharp pain in my head that gets worse with sunlight and improves with pressure... Or click "Dictate (EN)" to speak.'
                }
                value={description}
                onChange={handleChange}
              />

              <div className="flex justify-between items-center mt-1 text-xs text-base-content/60 sm:hidden">
                <span>{description.length} chars (min 10)</span>
              </div>

              <div className="card-actions justify-end mt-4">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}
                >
                  {submitting ? 'Analyzing with AI...' : existingDescription ? 'Add and calculate symptoms' : 'Analyze symptoms'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h3 className="card-title text-base">Tips for a good description</h3>
            <ul className="text-sm space-y-2 text-base-content/80 list-disc pl-5">
              <li>You can type or click <strong>Dictate (EN)</strong> to speak in English.</li>
              <li>Indicate when the symptoms first appeared.</li>
              <li>Describe sensations (sharp, burning, pressing...).</li>
              <li>Include exact location and possible radiations.</li>
              <li>Note what improves or worsens it (cold, heat, movement, pressure).</li>
              <li>Detail schedules or circumstances that aggravate it.</li>
              <li>Add accompanying symptoms (fever, thirst, mood, sleep).</li>
            </ul>
            <div className="alert alert-info mt-3 text-sm">
              <Sparkles size={16} />
              <span>AI converts text into structured repertory symptoms.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

